import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  useTheme
} from "@mui/material"
import AppAutocomplete from "components/AppAutocomplete"
import AppDatePicker from "components/AppDatePicker"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import ImageCropper from "components/ImageCropper"
import AppTextArea from "components/StyledComponents/AppTextArea"
import AppTextField from "components/StyledComponents/AppTextField"
import StyledLabel from "components/StyledComponents/StyledLabel"
import { getComponentType } from "helpers/functions"

import AddIngrediant from "pages/private/DVM_Console/DMV_Menu/AddDMVMenuItems/components/AddIngrediant"
import AddUniqueFact from "pages/private/DVM_Console/DMV_Menu/AddDMVMenuItems/components/AddUniqueFact"
import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from "react"
import { Col, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import {
  generateSoundbitesAction,
  uploadMenuItemAction,
  getSoundbiteVoiceIdsAction
} from "redux/marketing/actions"
import styles from "../../../menu.module.scss"
import AppButton from "components/StyledComponents/AppButton"
import PlayArrow from "@mui/icons-material/PlayArrowRounded"
import Pause from "@mui/icons-material/PauseRounded"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh"
import {
  aiEnhanceFieldAction,
  generateSoundbiteTextAction,
  removeImageBackgroundAction
} from "redux/marketing/actions/user"
import { marketingActions } from "redux/marketing/slice/user"
import AppTextFieldWithSelect from "components/StyledComponents/AppTextFieldWithSelect"

// Component types whose content can be AI-enhanced (others like image/date/price
// are excluded — those also come back in the API's non_ai_fields list).
const AI_FIELD_TYPES = ["textField", "textArea", "autoComplete", "uniqueFact", "ingredients"]

// Maps the active menu tab to the ai-enhance API's item_type (model switch).
const getAiItemType = (item) => {
  if (item === "bar menu") return "bar"
  if (item === "wine by glass") return "wine_glass"
  if (item === "wine by bottle") return "wine_bottle"
  return "food"
}

// Shapes the API's enhanced_value into what each field's component expects.
const transformEnhancedValue = (componentType, value) => {
  if (componentType === "uniqueFact") {
    const arr = Array.isArray(value) ? value : value != null ? [value] : []
    return arr.map((v) => ({ fact: typeof v === "string" ? v : v?.fact ?? "" }))
  }
  if (componentType === "ingredients") {
    const arr = Array.isArray(value) ? value : value != null ? [value] : []
    return arr.map((v) => (typeof v === "string" ? { name: v, measurement: "" } : v))
  }
  return value
}

const BasicDetailsForm = forwardRef(({ menuFields, formik, isEdit = false, saveBeforeAi }, ref) => {
  const theme = useTheme()
  const audioRef = useRef(null)
  const dispatch = useDispatch()
  const [isExpanded, setIsExpanded] = useState(true)
  const [uploadType, setUploadType] = useState("")
  const [openCrop, setCrop] = useState(false)
  const [fileDetails, setFileDetails] = useState(null)
  const [imageObjectURL, setImageObjectUrl] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const { menu_item_ID, soundbiteVoiceIds, linkCategory } = useSelector(
    (store) => store?.marketing?.menu || {}
  )
  const { soundbites } = useSelector((state) => state.marketing)
  const { nonAiFields } = useSelector((store) => store?.superAdmin || {})
  const [aiLoadingField, setAiLoadingField] = useState(null)
  const [removingBg, setRemovingBg] = useState(false)
  // Holds the generated cutout pending the user's confirmation; the dialog is
  // open whenever a removal is in progress (removingBg) or a preview is ready.
  const [bgPreview, setBgPreview] = useState(null)
  const [bgDialogOpen, setBgDialogOpen] = useState(false)
  // Media id of the image just uploaded in THIS form (local, so it can't leak
  // across items the way the global menuItemImageId does).
  const [uploadedImageId, setUploadedImageId] = useState(null)

  // Media id of the image currently shown for THIS item: a freshly uploaded
  // File carries its id in local state (uploadedImageId); an existing/edited or
  // already-cut-out image carries it on the value object (image.id).
  const currentImageMediaId =
    formik.values?.image instanceof File ? uploadedImageId : formik.values?.image?.id || null

  // Kicks off background removal for the current image and opens the preview
  // dialog (loading -> generated image for confirmation).
  const handleRemoveBackground = () => {
    if (!currentImageMediaId) return
    const mediaId = currentImageMediaId
    setBgPreview(null)
    setRemovingBg(true)
    setBgDialogOpen(true)
    dispatch(
      removeImageBackgroundAction({
        payload: { media_id: mediaId },
        onSuccess: (result) => {
          setBgPreview(result)
          setRemovingBg(false)
        },
        onError: () => {
          setRemovingBg(false)
          setBgDialogOpen(false)
        }
      })
    )
  }

  // Applies the previewed cutout to the form (only on explicit confirmation).
  const applyRemovedBackground = () => {
    if (!bgPreview) return
    formik.setFieldValue("image", { id: bgPreview.id, url: bgPreview.url })
    dispatch(marketingActions.setMenuImageId(bgPreview.id))
    setBgDialogOpen(false)
    setBgPreview(null)
  }

  const closeBgDialog = () => {
    if (removingBg) return // don't dismiss mid-generation
    setBgDialogOpen(false)
    setBgPreview(null)
  }

  useEffect(() => {
    dispatch(getSoundbiteVoiceIdsAction())
  }, [])

  const handleImageDelete = (formik) => {
    formik.setFieldValue("image", null)
    setUploadedImageId(null)
  }
  const onImageUpload = (image) => {
    setUploadType("")
    if (image) {
      setUploadType("image")
      setCrop(true)
      setFileDetails(image)
      setImageObjectUrl(URL.createObjectURL(image))
    }
  }

  function handleCroppedImage(image, formik) {
    if (uploadType === "image") {
      let data = new FormData()
      data.append("media", image)
      data.append("media_type", "IMAGE")
      data.append("content_type", image.type)
      image.preview = URL.createObjectURL(image)
      formik.setFieldValue("image", image)
      setUploadedImageId(null)
      dispatch(
        uploadMenuItemAction({
          data,
          onSuccess: (media) => setUploadedImageId(media?.id)
        })
      )
      setUploadType("")
    }
  }

  function showCropperModal(imageObjectURL, fileDetails, formik) {
    return (
      <ImageCropper
        setSelectedImage={(image) => handleCroppedImage(image, formik)}
        srcImage={imageObjectURL}
        fileDetail={fileDetails}
        aspectRatio={uploadType === "image" && 1}
        onClose={setCrop}
        openCrop={openCrop}></ImageCropper>
    )
  }

  const handleTimeChange = (e, field) => {
    const value = e?.target ? e.target.value : e
    formik.setFieldValue(field?.label, value)
  }

  const handleSoundbite = (soundbite) => {
    const payload = {
      text: soundbite,
      voice_id: formik.values?.soundbite_voice_id
    }
    dispatch(generateSoundbitesAction(payload))
    formik.setFieldTouched(`soundbite`, false)
  }

  const generateSoundText = () => {
    const payload = {
      menu_ids: [menu_item_ID]
    }
    dispatch(
      generateSoundbiteTextAction({
        payload,
        onSuccess: (content) => {
          formik.setFieldValue("soundbite", content)
          formik.setFieldValue("soundbite_url", "")
        }
      })
    )
  }

  const handleAiGenerate = (field, componentType) => {
    if (!menu_item_ID) return
    // Save the item first so the backend generates from the latest field data,
    // then run the AI enhance (per-field loading; the save itself blocks the page).
    const runEnhance = () => {
      setAiLoadingField(field.label)
      dispatch(
        aiEnhanceFieldAction({
          payload: {
            item_id: menu_item_ID,
            item_type: getAiItemType(linkCategory?.item),
            field: field.label
          },
          onSuccess: (enhancedValue) => {
            formik.setFieldValue(field.label, transformEnhancedValue(componentType, enhancedValue))
            setAiLoadingField(null)
          },
          onError: () => setAiLoadingField(null)
        })
      )
    }
    if (saveBeforeAi) {
      saveBeforeAi(formik, runEnhance)
    } else {
      runEnhance()
    }
  }

  const toggleSoundbite = () => {
    const soundbite_url = soundbites?.url || formik.values?.soundbite_url

    if (!soundbite_url) return

    // If already playing, pause it
    if (isPlaying && audioRef.current) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      // If audio exists, reset it
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }

      // Create new audio and play
      audioRef.current = new Audio(soundbite_url)
      audioRef.current.play()
      setIsPlaying(true)

      // When audio ends, reset state
      audioRef.current.onended = () => {
        setIsPlaying(false)
      }
    }
  }

  useImperativeHandle(ref, () => ({
    stopAudio: () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
        audioRef.current = null
      }
    }
  }))

  const notificationDateField = menuFields?.find((f) => f.label === "notification_date")
  const notificationTimeField = menuFields?.find((f) => f.label === "notification_time")

  return (
    <div>
      {showCropperModal(imageObjectURL, fileDetails, formik)}
      <Dialog open={bgDialogOpen} onClose={closeBgDialog} maxWidth="sm" fullWidth>
        <DialogContent
          sx={{ backgroundColor: theme.palette.background.default, borderRadius: "12px", p: 4 }}>
          <Typography variant="h6" sx={{ mb: 3, textAlign: "center" }}>
            Remove Background
          </Typography>
          {removingBg ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                minHeight: 260
              }}>
              <CircularProgress sx={{ color: theme.palette.secondary.main }} />
              <Typography variant="body1Regular" sx={{ color: theme.palette.text.secondary }}>
                Removing background…
              </Typography>
            </Box>
          ) : bgPreview ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 260,
                  borderRadius: "12px",
                  // checkerboard so the removed (transparent) background is obvious
                  backgroundColor: "#fff",
                  backgroundImage:
                    "linear-gradient(45deg,#d9d9d9 25%,transparent 25%),linear-gradient(-45deg,#d9d9d9 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#d9d9d9 75%),linear-gradient(-45deg,transparent 75%,#d9d9d9 75%)",
                  backgroundSize: "20px 20px",
                  backgroundPosition: "0 0,0 10px,10px -10px,-10px 0",
                  p: 2,
                  mb: 3
                }}>
                <img
                  src={bgPreview.url}
                  alt="Background removed preview"
                  style={{ maxWidth: "100%", maxHeight: 320, objectFit: "contain" }}
                />
              </Box>
              <Typography
                variant="body1Regular"
                sx={{ textAlign: "center", mb: 3, color: theme.palette.text.secondary }}>
                Keep this background-removed image?
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <AppButton
                  styles={{ padding: "10px 24px", backgroundColor: "transparent" }}
                  onClick={closeBgDialog}>
                  Discard
                </AppButton>
                <AppButton styles={{ padding: "10px 24px" }} onClick={applyRemovedBackground}>
                  Use This Image
                </AppButton>
              </Box>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
      <Box
        flexDirection="column"
        // className="mt-4"
        className={styles.accordian_container}
        sx={{
          backgroundColor: isExpanded
            ? theme.palette.background.main
            : theme.palette.background.default,
          paddingBottom: !isExpanded && "0px !important",
          margin: isExpanded && "36px 0px !important"
        }}>
        <Accordion expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
          <AccordionSummary className={styles.summary}>
            <Box display="flex" alignItems="center" gap={1}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              <Typography variant="bodyLyon">Menu Item</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {menuFields?.map((field, index) => {
              const componentType = getComponentType(field.label)
              const fieldElement = (() => {
                switch (componentType) {
                  case "textField":
                    return (
                      <Row className="w-100 m-0 mt-4" key={index}>
                        <Col lg="12">
                          <AppTextField
                            id={field.label}
                            name={field.label}
                            type="text"
                            fullWidth
                            isRequired={field.label === "name" ? true : false}
                            label={field.fieldName}
                            isUpperCase={!false}
                            placeholder={
                              `Enter ${field.fieldName}` +
                              (field?.label === "arriving_day" ? " (e.g. YYYY-MM-DD)" : "")
                            }
                            value={formik.values[field.label]}
                            onBlur={formik.handleBlur(field.label)}
                            onChange={formik.handleChange}
                            error={Boolean(formik.errors[field.label])}
                            touched={formik.touched[field.label]}
                            helperText={formik.errors[field.label]}
                          />
                        </Col>
                      </Row>
                    )
                  case "imageUpload": {
                    // Every item's image field gets a "Remove BG" action (next to the
                    // field, like the AI buttons) once an image is present.
                    const showRemoveBg = !!currentImageMediaId
                    return (
                      <Row className="w-100 m-0 mt-4" key={index}>
                        <Col lg="12">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 8,
                              width: "100%"
                            }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <ImageUploadField
                                label={field.fieldName}
                                isVariant={true}
                                placeholder={`Upload ${field.fieldName}`}
                                value={formik.values[field.label]}
                                onChange={(e) => onImageUpload(e, formik)}
                                onDelete={() => handleImageDelete(formik)}
                                error={formik.errors[field.label]}
                                touched={formik.touched[field.label]}
                              />
                            </div>
                            {showRemoveBg && (
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                {/* Invisible label-height spacer so the button lines up
                                    with the upload box. */}
                                <StyledLabel
                                  variant="body1Regular"
                                  aria-hidden
                                  style={{ visibility: "hidden" }}>
                                  &nbsp;
                                </StyledLabel>
                                <AppButton
                                  styles={{
                                    height: "50px",
                                    padding: "0 16px",
                                    fontSize: "13px",
                                    whiteSpace: "nowrap",
                                    minWidth: "132px",
                                    justifyContent: "center"
                                  }}
                                  disabled={removingBg || bgDialogOpen}
                                  onClick={handleRemoveBackground}>
                                  <AutoFixHighIcon sx={{ fontSize: 16, mr: 0.7 }} />
                                  Remove BG
                                </AppButton>
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    )
                  }
                  case "autoComplete":
                    return (
                      <Row className="w-100 m-0 mt-4" key={index}>
                        <Col lg="12">
                          <AppAutocomplete
                            id={field.label}
                            isUpperCase={true}
                            placeholder={`Enter ${field.fieldName}`}
                            label={field.fieldName}
                            value={formik.values[field.label]}
                            error={Boolean(formik.errors[field.label])}
                            helperText={formik.errors[field.label]}
                            touched={formik.touched[field.label]}
                            onChange={(event, newValue) =>
                              formik.setFieldValue(field.label, newValue)
                            }
                          />
                        </Col>
                      </Row>
                    )
                  case "uniqueFact":
                    return (
                      <AddUniqueFact
                        key={index}
                        formik={formik}
                        values={formik.values?.[field.label] || []} // Ensure values is an array
                        name={field.label}
                        buttonText={`+ Add ${field.fieldName}`}
                        placeholder={`Enter ${field.fieldName}`}
                        label={field.fieldName}
                        fullWidth
                      />
                    )
                  case "textArea":
                    return (
                      <Row className="w-100 m-0 mt-4" key={index}>
                        <Col lg="12">
                          <AppTextArea
                            label={field?.fieldName}
                            minRows={5}
                            maxRows={15}
                            placeholder={`Enter ${field?.fieldName}`}
                            value={formik.values[field.label]}
                            onChange={formik.handleChange(field.label)}
                            onBlur={formik.handleBlur(field.label)}
                            error={Boolean(formik.errors[field.label])}
                            touched={formik.touched[field.label]}
                            helperText={formik.errors[field.label]}
                          />
                        </Col>
                      </Row>
                    )
                  case "numberField":
                    return (
                      <Row className="w-100 m-0 mt-4" key={index}>
                        <Col lg="12">
                          <AppTextField
                            id={field.label}
                            name={field.label}
                            type="number"
                            fullWidth
                            label={field.fieldName}
                            isUpperCase={!false}
                            placeholder={`Enter ${field.fieldName}`}
                            value={formik.values[field.label]}
                            onChange={formik.handleChange(field.label)}
                            onBlur={formik.handleBlur(field.label)}
                            error={Boolean(formik.errors[field.label])}
                            touched={formik.touched[field.label]}
                            helperText={formik.errors[field.label]}
                          />
                        </Col>
                      </Row>
                    )
                  // Notification date/time are rendered together below the loop
                  // (see the grouped notification Row) so the two boxes stay side by side.
                  case "datePicker":
                  case "timeField":
                    return null
                  case "ingredients":
                    return (
                      <AddIngrediant
                        key={index}
                        formik={formik}
                        values={formik.values?.[field?.label]}
                        name={field?.label}
                        buttonText={`+ Add ${field?.fieldName}`}
                        label={field?.fieldName}
                        fullWidth
                      />
                    )
                  default:
                    return null
                }
              })()
              const showGenerate =
                isEdit &&
                !!menu_item_ID &&
                !nonAiFields?.includes(field.label) &&
                AI_FIELD_TYPES.includes(componentType)
              if (!showGenerate) {
                return <React.Fragment key={index}>{fieldElement}</React.Fragment>
              }
              const fieldValue = formik.values?.[field.label]
              const hasContent = Array.isArray(fieldValue)
                ? fieldValue.length > 0
                : typeof fieldValue === "string"
                ? fieldValue.trim() !== ""
                : fieldValue !== null && fieldValue !== undefined && fieldValue !== ""
              const isFieldLoading = aiLoadingField === field.label
              return (
                <div
                  key={index}
                  style={{ display: "flex", alignItems: "flex-start", gap: 8, width: "100%" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>{fieldElement}</div>
                  {/* Match the field's top margin (mt-4) + an invisible label-height spacer
                      so the button lines up with the input box, not the label. */}
                  <div className="mt-4" style={{ display: "flex", flexDirection: "column" }}>
                    <StyledLabel
                      variant="body1Regular"
                      aria-hidden
                      style={{ visibility: "hidden" }}>
                      &nbsp;
                    </StyledLabel>
                    <AppButton
                      styles={{
                        height: "50px",
                        padding: "0 16px",
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                        minWidth: "132px",
                        justifyContent: "center"
                      }}
                      disabled={isFieldLoading}
                      onClick={() => handleAiGenerate(field, componentType)}>
                      <AutoAwesomeIcon sx={{ fontSize: 16, mr: 0.7 }} />
                      {isFieldLoading
                        ? hasContent
                          ? "Refining..."
                          : "Generating..."
                        : hasContent
                        ? "Refine"
                        : "Generate"}
                    </AppButton>
                  </div>
                </div>
              )
            })}
            {(notificationDateField || notificationTimeField) && (
              <Row className="w-100 m-0 mt-4">
                {notificationDateField && (
                  <Col lg="6">
                    {/* Mirror AppTextField's structure (Grid container > label > Grid item)
                        and box (50px tall, 12/16 padding) so the date lines up with the
                        time field exactly. */}
                    <Grid container className="datesContainer">
                      <StyledLabel variant="body1Regular">
                        {notificationDateField.fieldName}
                      </StyledLabel>
                      <Grid item xs={12}>
                        <Box
                          className="dateFieldContainer w-100"
                          sx={{
                            "& .MuiInputBase-root": { height: "50px !important" },
                            "& .MuiInputBase-input": { padding: "12px 16px !important" }
                          }}>
                          <AppDatePicker
                            minDate={new Date()}
                            maxDate={new Date().setMonth(new Date().getMonth() + 5)}
                            inputFormat="dd MMM yyyy"
                            value={formik.values[notificationDateField.label]}
                            onChange={(val) =>
                              formik.setFieldValue(notificationDateField.label, val)
                            }
                            error={formik.errors[notificationDateField.label]}
                            touched={formik.touched[notificationDateField.label]}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Col>
                )}
                {notificationTimeField && (
                  <Col lg="6">
                    <AppTextField
                      id={notificationTimeField.label}
                      type="time"
                      fullWidth
                      label={notificationTimeField.fieldName}
                      isUpperCase={true}
                      value={formik.values?.[notificationTimeField.label]}
                      onChange={(e) => handleTimeChange(e, notificationTimeField)}
                      error={
                        formik.errors?.[notificationTimeField.label] &&
                        formik.touched?.[notificationTimeField.label]
                      }
                      helperText={
                        formik.touched?.[notificationTimeField.label] &&
                        formik.errors?.[notificationTimeField.label]
                      }
                    />
                  </Col>
                )}
              </Row>
            )}
            <Row className="w-100 m-0 mt-4">
              <Col lg="12">
                <AppTextArea
                  label="Soundbite"
                  minRows={5}
                  maxRows={15}
                  placeholder={`Enter Soundbite`}
                  value={formik.values?.soundbite}
                  onChange={formik.handleChange("soundbite")}
                  onBlur={formik.handleBlur("soundbite")}
                  error={Boolean(formik.errors?.soundbite)}
                  touched={formik.touched?.soundbite}
                  helperText={formik.errors?.soundbite}
                />
              </Col>
              <Row
                className="w-100 m-0 mt-2 mb-2  align-items-center"
                style={{ justifyContent: "space-between" }}>
                <Col lg="4" className="p-0">
                  <AppTextFieldWithSelect
                    label="Soundbite Voice Id"
                    name="soundbite_voice_id"
                    select={true}
                    isUpperCase={true}
                    placeholder="Select Voice"
                    menuItems={
                      (soundbiteVoiceIds?.length > 0 &&
                        soundbiteVoiceIds?.map((voice) => ({
                          label: voice.name,
                          value: voice.voice_id
                        }))) ||
                      []
                    }
                    value={formik.values.soundbite_voice_id}
                    error={formik.errors.soundbite_voice_id}
                    touched={formik.touched?.soundbite_voice_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur("soundbite_voice_id")}
                  />
                </Col>
                <Col lg="8" className="mt-3">
                  <Row className="w-100 m-0 mt-4 mb-2 justify-content-end align-items-center">
                    <Col lg="3" className="d-flex justify-content-end p-0">
                      {(soundbites?.url || formik.values?.soundbite_url) &&
                        !formik.touched?.soundbite && (
                          <AppButton
                            styles={{ padding: "10px 16px", fontSize: "13px" }}
                            onClick={toggleSoundbite}>
                            {isPlaying ? (
                              <>
                                <Pause sx={{ height: 24, width: 24, marginRight: 1 }} />
                                Stop
                              </>
                            ) : (
                              <>
                                <PlayArrow sx={{ height: 24, width: 24, marginRight: 1 }} />
                                Play
                              </>
                            )}
                          </AppButton>
                        )}
                    </Col>
                    <Col lg={isEdit ? "9" : "5"} className="d-flex justify-content-end p-0">
                      {isEdit && (
                        <AppButton
                          sx={{ padding: "13px 16px", mr: 0.7, fontSize: "13px" }}
                          onClick={() => generateSoundText()}>
                          Generate Sound Text
                        </AppButton>
                      )}
                      <AppButton
                        sx={{ padding: "13px 16px", fontSize: "13px" }}
                        disabled={!formik.values?.soundbite}
                        onClick={() => handleSoundbite(formik.values?.soundbite)}>
                        Generate Sound Bite
                      </AppButton>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Row>
          </AccordionDetails>
        </Accordion>
      </Box>
    </div>
  )
})
BasicDetailsForm.displayName = "BasicDetailsForm"

export default BasicDetailsForm
